import {
  CanoeIcon,
  HiddenIcon,
  LayoutBlockIcon,
  LayoutFlexIcon,
  LayoutGridIcon,
  LayoutInlineBlockIcon,
} from 'canoe';
import React from 'react';

import { ValueBox, ValueGroup, ValueToggle } from '@candu-ai/craft/ui';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { Section } from 'craft/containers/Toolbox/components';
import { useMultiNodes, useResolver, useResponsiveCss } from 'craft/hooks';
import { CommonNodeProperties, CssSelectorId } from 'craft/types';

import { BlockedDisplayWarning } from './BlockedDisplayWarning';
import { Flex } from './Flex';

export type Display = 'block' | 'flex' | 'inline-block' | 'grid' | 'none';

const data: { value: Omit<Display, 'grid'>; tooltip: string; icon: CanoeIcon }[] = [
  {
    value: 'block',
    tooltip: 'Block',
    icon: LayoutBlockIcon,
  },
  {
    value: 'flex',
    tooltip: 'Flex',
    icon: LayoutFlexIcon,
  },
  {
    value: 'inline-block',
    tooltip: 'Inline Block',
    icon: LayoutInlineBlockIcon,
  },
  {
    value: 'none',
    tooltip: 'Hidden',
    icon: HiddenIcon,
  },
];

const gridData: {
  value: Omit<Display, 'grid'> | Omit<Display, 'block' | 'flex' | 'inline-block'>;
  tooltip: string;
  icon: CanoeIcon;
}[] = [
  {
    value: 'grid',
    tooltip: 'Grid',
    icon: LayoutGridIcon,
  },
  {
    value: 'none',
    tooltip: 'Hidden',
    icon: HiddenIcon,
  },
];

const getData = (
  type?: string,
): {
  value: Omit<Display, 'block' | 'flex' | 'inline-block'>;
  tooltip: string;
  icon: CanoeIcon;
}[] => {
  if (type === BuiltInComponents.Columns) {
    return gridData;
  }
  return data;
};

const flexProperties = [
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'columnGap',
  'rowGap',
];

const clearFlexPropertiesObject = Object.fromEntries(flexProperties.map((p) => [p, undefined]));

const withFlexProperties = (display?: string) => {
  if (display === 'flex') {
    return { display };
  }
  return { display, ...clearFlexPropertiesObject };
};

interface Props {
  nodeIds: string[];
  cssSelectorId?: CssSelectorId;
  defaultValue?: Display;
}

export const Display = React.memo(({ nodeIds, cssSelectorId, defaultValue }: Props) => {
  const { type } = useMultiNodes(nodeIds, () => null, CommonNodeProperties.Multiple);
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(
    nodeIds,
    CommonNodeProperties.Multiple,
    cssSelectorId,
  );
  const setDisplay = (display?: Display) => setResponsiveCss(withFlexProperties(display));
  const { display = defaultValue || 'block' } = responsiveCss || {};
  const resolver = useResolver(type);
  const blockedDisplays = resolver?.toolbox?.schema?.Layout?.blockedDisplays || [];

  if (!type || type === BuiltInComponents.Multiple) {
    return null;
  }

  return (
    <Section name="Layout" data-cy="toolbox-section-layout">
      <ValueGroup>
        <ValueBox label="Display">
          {getData(type).map((item) => (
            <ValueToggle
              key={item.value as string}
              isActive={display === item.value}
              icon={item.icon}
              disabled={blockedDisplays.includes(item.value as Display)}
              tooltip={item.tooltip}
              onClick={() => setDisplay(item.value as Display)}
            />
          ))}
        </ValueBox>
      </ValueGroup>
      {blockedDisplays.length > 0 && !nodeIds.includes(CRAFT_ROOT_ID) && (
        <BlockedDisplayWarning type={type} />
      )}
      {display === 'flex' && <Flex nodeIds={nodeIds} cssSelectorId={cssSelectorId} />}
    </Section>
  );
});

Display.displayName = 'Display';
